import React from 'react';
import { styles, STATUS_CONFIG } from '../styles';
import { Icons } from './Icons';

/**
 * PatientCard Component
 * Displays patient info with expandable procedure list
 *
 * Props:
 * - data: { name, status, procedures: [{ id, procedure_type, start_time, end_time, status }] }
 * - index: number (for staggered animation)
 * - expanded: boolean
 * - onToggle: () => void
 * - onStatusChange: (patientId, newStatus) => void
 * - onDelete: () => void
 */
function PatientCard({ data, index, expanded, onToggle, onStatusChange, onDelete }) {
  // Get status config (colors, labels)
  const sc = STATUS_CONFIG[data.status] || STATUS_CONFIG.PENDING;
  const isActive = data.status === 'IN_PROGRESS';
  const isDone = data.status === 'COMPLETED';

  // Get initials from name (last 2 characters)
  const initials = data.name
    .split(' ')
    .map(w => w[0])
    .join('')
    .slice(-2)
    .toUpperCase();

  // Dynamic card styles based on status
  const cardStyle = {
    ...styles.card,
    borderLeft: `3px solid ${sc.color}`,
    background: isActive ? '#fafcff' : isDone ? '#fafafa' : '#fff',
    boxShadow: isActive
      ? '0 4px 24px rgba(37,99,235,0.08)'
      : '0 1px 3px rgba(0,0,0,0.04)',
    opacity: isDone ? 0.65 : 1,
    animationDelay: `${index * 40}ms`,
  };

  return (
    <div style={cardStyle}>
      {/* Card Top Section */}
      <div style={styles.cardTop}>
        {/* Avatar */}
        <div style={{ ...styles.avatar, background: sc.bg, color: sc.color }}>
          {isDone
            ? Icons.check({ size: 20, color: sc.color })
            : <span>{initials}</span>
          }
        </div>

        {/* Info */}
        <div style={styles.cardInfo}>
          <div style={styles.cardNameRow}>
            <span style={{
              ...styles.cardName,
              textDecoration: isDone ? 'line-through' : 'none',
              color: isDone ? '#94a3b8' : '#0f172a'
            }}>
              {data.name}
            </span>
            <span style={{ ...styles.statusBadge, background: sc.bg, color: sc.color }}>
              {sc.label}
            </span>
          </div>
          <div style={styles.cardMeta}>
            {Icons.clock({ size: 12, color: '#94a3b8' })}
            <span>{data.procedures[0]?.start_time || '--:--'}</span>
            <span style={styles.metaDot}>•</span>
            <span>{data.procedures.length} thủ thuật</span>
          </div>
        </div>

        {/* Actions */}
        <div style={styles.cardActions}>
          {data.status === 'PENDING' && (
            <button
              onClick={() => onStatusChange(data.id, 'IN_PROGRESS')}
              style={styles.btnStart}
            >
              {Icons.play({ size: 13, color: '#2563eb' })}
              <span>Bắt đầu</span>
            </button>
          )}
          {data.status === 'IN_PROGRESS' && (
            <button
              onClick={() => onStatusChange(data.id, 'COMPLETED')}
              style={styles.btnDone}
            >
              {Icons.check({ size: 14, color: '#fff' })}
              <span>Xong</span>
            </button>
          )}
          {isDone && (
            <span style={styles.doneLabel}>Hoàn tất</span>
          )}
          <button onClick={onToggle} style={styles.btnExpand}>
            {Icons.chevDown({ size: 16, color: '#94a3b8' })}
          </button>
        </div>
      </div>

      {/* Expanded Section: Procedure List */}
      {expanded && (
        <div style={styles.cardExpanded}>
          <div style={styles.procList}>
            {data.procedures.map((pr) => {
              const psc = STATUS_CONFIG[pr.status] || STATUS_CONFIG.PENDING;
              return (
                <div key={pr.id} style={styles.procRow}>
                  <div style={{ ...styles.procDot, background: psc.color }} />
                  <span style={styles.procRowName}>{pr.procedure_type}</span>
                  <span style={styles.procRowTime}>{pr.start_time} – {pr.end_time}</span>
                  <span style={{ ...styles.procRowStatus, color: psc.color }}>
                    {psc.label}
                  </span>
                </div>
              );
            })}
          </div>
          <div style={styles.cardExpandedFooter}>
            <button onClick={onDelete} style={styles.btnDeleteSmall}>
              {Icons.trash({ size: 13, color: '#ef4444' })}
              <span>Xoá</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PatientCard;
